import { FC, ReactNode, useState } from "react";
import { Button } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { StepProps } from "src/Modules/Flights/constants/JourneyDetails";
import PaymentOptions from "./components/PaymentOptions";
import PaymobPayment from "./components/PaymobPayment/PaymobPayment";
import { useInitiateOrder, computeBookingFlags, hasBookingFlags } from "./hooks/useInitiateOrder";
import { PAYMENT_FLOWS, PAYMENT_METHODS, type FailContext } from "./constants";
import { useHotelDynamicDetails } from "../../context";

const Payment: FC<StepProps> = (_) => {
  const { pointsFactor, payment } = useHotelDynamicDetails();

  const navigate = useNavigate();
  const { searchId, hotelDetailsKey } = useParams();
  const { runInitiate, isLoading: isInitiateRunning, returnUrl } = useInitiateOrder();
  const { selectedPayment, mixError } = payment;
  const [failureNode, setFailureNode] = useState<ReactNode>(null);
  const [isPaymentStep, setIsPaymentStep] = useState(false);
  const [paymentData, setPaymentData] = useState<{ provider: string; clientSecret: string }>();

  const handleConfirmSelection = async () => {
    setFailureNode(null);
    if (!selectedPayment || mixError) return;
    const usedFlow = PAYMENT_FLOWS.find((flow) => flow.check(selectedPayment));
    if (!usedFlow) return;
    const res = await usedFlow.execute(runInitiate, selectedPayment);
    if (res?.data?.referenceNumber && !hasBookingFlags(res?.data)) {
      setPaymentData({provider:"paymob", clientSecret:res.data.clientSecret});
      usedFlow.onSuccess({
        navigate,
        hotelDetailsKey,
        returnURL: returnUrl,
        referenceNumber: res.data.referenceNumber,
        setIsPaymentStep,
      });
    } else {
      const flags = computeBookingFlags(res?.data, res?.error);
      const newFare = (res?.data as any)?.rooms?.[0]?.convertedPrice ?? 0;
      const failCtx: FailContext = {
        goToListing: () => navigate(`/hotel/${searchId}`,{replace:true}),
        reBook: handleConfirmSelection,
        getFare: () => ({ oldFare: 0, newFare: flags.isPriceChanged ? newFare : 0 }),
        isLoading: isInitiateRunning,
        flags,
      };
      setFailureNode(usedFlow.onFail?.(failCtx) ?? null);
    }
  };

  return (
    <>
      {isPaymentStep ? (
        <PaymobPayment paymentData={paymentData} setIsPaymentStep={setIsPaymentStep} selectedPayment={selectedPayment} currency={"EGP"} />
      ) : (
        <div className="flex w-full flex-col gap-4">
          <PaymentOptions payment={payment} currency={"EGP"} pointsFactor={pointsFactor} disableAll={isInitiateRunning} />
          <div className="flex items-center justify-center">
            <Button
              onClick={handleConfirmSelection}
              disabled={!selectedPayment || !!mixError}
              loading={isInitiateRunning}
              iconPlacement="end"
              className="bg-background-active! w-full! rounded-md! p-6! font-semibold! cursor-pointer! text-font-inverted! shadow-md! disabled:cursor-not-allowed! disabled:opacity-50!"
            >
              {selectedPayment && PAYMENT_METHODS[selectedPayment.paymentMethod].buttonText}
            </Button>
          </div>
        </div>
      )}
      {failureNode}
    </>
  );
};

export default Payment;
